// src/context/AuthContext.js — Global auth state provider
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { STORAGE_KEYS } from '../config/env';

const KEY = STORAGE_KEYS.auth;

function parseAuthStorageValue(value) {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    return parsed?.token ? parsed : null;
  } catch {
    return null;
  }
}

function authIdentity(value) {
  const userIdentity = value?.user?.id
    ?? value?.user?.email
    ?? value?.user?.account
    ?? null;
  return JSON.stringify([value?.token ?? null, userIdentity]);
}

export function getAuthenticatedUserId(value) {
  return String(
    value?.user?.id
    ?? value?.user?.email
    ?? value?.user?.account
    ?? '',
  ).trim();
}

function clearUserOperationState(value) {
  const userId = getAuthenticatedUserId(value);
  if (!userId) return;
  const userNamespace = encodeURIComponent(userId);
  const operationPrefixes = [
    `data-input:${userNamespace}:`,
    `ef-sync:${userNamespace}:`,
  ];
  const matchingKeys = [];
  for (let keyIndex = 0; keyIndex < sessionStorage.length; keyIndex += 1) {
    const storageKey = sessionStorage.key(keyIndex);
    if (operationPrefixes.some(prefix => storageKey?.startsWith(prefix))) {
      matchingKeys.push(storageKey);
    }
  }
  matchingKeys.forEach((storageKey) => sessionStorage.removeItem(storageKey));
}

function readStoredAuth() {
  try {
    const local = localStorage.getItem(KEY);
    if (local) {
      const parsed = JSON.parse(local);
      if (parsed?.token) return parsed;
      localStorage.removeItem(KEY);
    }
    const session = sessionStorage.getItem(KEY);
    if (session) {
      const parsed = JSON.parse(session);
      if (parsed?.token) return parsed;
      sessionStorage.removeItem(KEY);
    }
  } catch (e) { /* ignore */ }
  return null;
}

export function getStoredToken() {
  const auth = readStoredAuth();
  return auth?.token || null;
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [auth, setAuth] = useState(readStoredAuth);
  const [sessionExpired, setSessionExpired] = useState(false);

  const login = useCallback(({ user, token, remember }) => {
    const session = {
      user,
      token,
      signedInAt: new Date().toISOString(),
      remember,
      auth_method: user?.auth_method ?? 'password',
    };
    try {
      if (getAuthenticatedUserId(auth) !== getAuthenticatedUserId(session)) {
        clearUserOperationState(auth);
      }
      if (remember) {
        localStorage.setItem(KEY, JSON.stringify(session));
        sessionStorage.removeItem(KEY);
      } else {
        sessionStorage.setItem(KEY, JSON.stringify(session));
        localStorage.removeItem(KEY);
      }
    } catch (e) { /* ignore */ }
    setAuth(session);
    setSessionExpired(false);
  }, [auth]);

  const logout = useCallback(() => {
    try {
      clearUserOperationState(auth);
      localStorage.removeItem(KEY);
      sessionStorage.removeItem(KEY);
    } catch (e) { /* ignore */ }
    setAuth(null);
  }, [auth]);

  // Listen for token expiry fired by axiosClient interceptor (same tab)
  useEffect(() => {
    const handler = () => { logout(); setSessionExpired(true); };
    window.addEventListener('carbon:unauthorized', handler);
    return () => window.removeEventListener('carbon:unauthorized', handler);
  }, [logout]);

  // Keep this tab aligned with auth changes made in another tab.
  useEffect(() => {
    const handler = (e) => {
      if (e.key !== KEY) return;
      const nextAuth = parseAuthStorageValue(e.newValue);
      if (authIdentity(auth) === authIdentity(nextAuth)) {
        setAuth(nextAuth);
        return;
      }
      try {
        clearUserOperationState(auth);
        sessionStorage.removeItem(KEY);
      } catch (error) { /* ignore */ }
      setAuth(nextAuth);
      setSessionExpired(false);
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [auth]);

  return (
    <AuthContext.Provider value={{ auth, login, logout, sessionExpired }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthContext must be used within AuthProvider');
  return ctx;
}
